(() => {
  function normalizeCampaign(data) {
    if (!data || data.enabled !== true || typeof data.campaignId !== 'string' || !data.campaignId ||
        !Array.isArray(data.tiers) || !data.tiers.length || data.tiers.length > 50 ||
        data.tiers.some(tier => !tier || typeof tier !== 'object')) return null;
    const tiers = data.tiers.map(tier => ({
      minimum: Number(tier.minimumQuantity), percentage: Number(tier.percentage),
      startsAt: tier.startsAt == null ? null : Number(tier.startsAt),
      endsAt: tier.endsAt == null ? null : Number(tier.endsAt)
    })).sort((a, b) => a.minimum - b.minimum);
    if (tiers.some((tier, index) => !Number.isSafeInteger(tier.minimum) || tier.minimum < 1 ||
        !Number.isFinite(tier.percentage) || tier.percentage <= 0 || tier.percentage > 100 ||
        Math.abs(tier.percentage * 100 - Math.round(tier.percentage * 100)) > 0.000001 ||
        (tier.startsAt !== null && !Number.isFinite(tier.startsAt)) ||
        (tier.endsAt !== null && !Number.isFinite(tier.endsAt)) ||
        (tier.startsAt !== null && tier.endsAt !== null && tier.endsAt <= tier.startsAt) ||
        (index > 0 && (tier.minimum === tiers[index - 1].minimum || tier.percentage <= tiers[index - 1].percentage)))) return null;
    return { id: data.campaignId, tiers };
  }

  function activeTiers(campaign, now) {
    return campaign.tiers.filter(tier => (tier.startsAt === null || now >= tier.startsAt) &&
      (tier.endsAt === null || now < tier.endsAt));
  }

  function discountedPrice(price, percentage) {
    return Math.round(price * (10000 - Math.round(percentage * 100)) / 10000);
  }

  function priceRows(price, tiers) {
    const rows = tiers.map((tier, index) => ({ ...tier,
      maximum: tiers[index + 1] ? tiers[index + 1].minimum - 1 : null,
      price: discountedPrice(price, tier.percentage)
    }));
    if (tiers.length && tiers[0].minimum > 1) rows.unshift({ minimum: 1, maximum: tiers[0].minimum - 1, percentage: 0, price });
    return rows;
  }

  function campaignQuantity(items, campaignId) {
    return items.reduce((total, item) => total + (item.campaignId === campaignId &&
      Number.isSafeInteger(item.quantity) && item.quantity > 0 ? item.quantity : 0), 0);
  }

  function estimate(price, tiers, cartQuantity, selectedQuantity) {
    const projected = cartQuantity + selectedQuantity;
    const current = tiers.filter(tier => projected >= tier.minimum).at(-1);
    const percentage = current ? current.percentage : 0;
    const unitPrice = discountedPrice(price, percentage);
    return { projected, percentage, unitPrice, saving: (price - unitPrice) * selectedQuantity,
      next: tiers.find(tier => tier.minimum > projected) || null };
  }

  function cartCampaigns(items, campaigns, now) {
    const seen = new Set();
    return campaigns.flatMap(data => {
      const campaign = normalizeCampaign(data);
      if (!campaign || seen.has(campaign.id)) return [];
      seen.add(campaign.id);
      const quantity = campaignQuantity(items, campaign.id);
      const tiers = activeTiers(campaign, now);
      if (!quantity || !tiers.length) return [];
      const result = estimate(0, tiers, quantity, 0);
      return [{ id: campaign.id, name: typeof data.name === 'string' ? data.name : '', quantity,
        percentage: result.percentage, next: result.next, maximum: result.next?.minimum || tiers.at(-1).minimum }];
    });
  }

  function boundaryDelay(campaigns, now) {
    const boundaries = campaigns.flatMap(data => normalizeCampaign(data)?.tiers || [])
      .flatMap(tier => [tier.startsAt, tier.endsAt]).filter(time => time !== null && time > now);
    return boundaries.length ? Math.min(Math.max(50, Math.min(...boundaries) - now + 20), 2147483647) : null;
  }

  function validCartState(data) {
    return Boolean(data && Array.isArray(data.items) && Number.isFinite(data.serverNow) &&
      (data.campaigns === undefined || Array.isArray(data.campaigns)) &&
      data.items.every(item => item && typeof item.campaignId === 'string' && item.campaignId &&
        Number.isSafeInteger(item.quantity) && item.quantity >= 0 &&
        (item.variantId === undefined || typeof item.variantId === 'string')));
  }

  const core = { normalizeCampaign, activeTiers, discountedPrice, priceRows, campaignQuantity, estimate,
    cartCampaigns, boundaryDelay, validCartState };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('quantity-discount-table')) return;

  const cartState = {
    data: null, snapshot: null, status: 'idle', listeners: new Set(), controller: null, timeout: null, version: 0,
    notify(listener) {
      const metadata = { status: this.status, snapshot: this.snapshot };
      if (listener) listener(this.data, metadata);
      else this.listeners.forEach(callback => callback(this.data, metadata));
    },
    subscribe(listener) {
      this.listeners.add(listener);
      if (this.status !== 'idle') this.notify(listener);
      else if (!this.controller) this.refresh();
      return () => this.listeners.delete(listener);
    },
    refresh() {
      if (!this.listeners.size) return;
      this.version += 1;
      const version = this.version;
      this.controller?.abort();
      clearTimeout(this.timeout);
      const controller = new AbortController();
      this.controller = controller;
      this.data = null;
      this.status = 'loading';
      this.notify();
      this.timeout = setTimeout(() => {
        if (version !== this.version) return;
        controller.abort();
        this.status = 'error';
        this.notify();
      }, 8000);
      const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
      url.searchParams.set('section_id', 'quantity-discount-cart-state');
      url.searchParams.set('_qd', String(Date.now()));
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      fetch(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
        .then(response => {
          if (!response.ok || new URL(response.url).origin !== window.location.origin) throw new Error('Cart state unavailable');
          return response.text();
        })
        .then(html => {
          const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-quantity-discount-cart-state]');
          const data = JSON.parse(node?.textContent || 'null');
          if (!validCartState(data)) throw new Error('Invalid cart state');
          if (version !== this.version) return;
          if (controller.signal.aborted) throw new Error('Cart state request expired');
          this.data = { ...data, receivedAt: performance.now() };
          this.snapshot = this.data;
          this.status = 'ready';
          this.notify();
        })
        .catch(() => {
          if (version === this.version && this.status !== 'error') {
            this.status = 'error';
            this.notify();
          }
        })
        .finally(() => {
          if (version === this.version) {
            clearTimeout(this.timeout);
            this.controller = null;
          }
        });
    }
  };

  // Cached values reserve the layout only: masked nodes are hidden from AT.
  function skeleton(node, loading) {
    if (!node) return;
    node.classList.toggle('qd-skeleton', loading);
    node.setAttribute('aria-hidden', String(loading));
  }

  function loadingRegion(host, region, loading, label) {
    if (region) {
      region.setAttribute('aria-busy', String(loading));
      if (region.style) {
        if (loading && !host.loading) {
          const height = region.getBoundingClientRect?.().height;
          if (height > 0) region.style.minHeight = height + 'px';
        } else if (!loading) region.style.minHeight = '';
      }
    }
    const status = host.querySelector('[data-qd-loading-status]');
    if (status && loading !== Boolean(host.loading)) status.textContent = loading ? label : '';
    host.loading = loading;
  }

  const cartEvents = { updated: 0, visibility: 0 };
  document.addEventListener('utopia:cart-updated', () => { cartEvents.updated += 1; cartState.refresh(); });
  document.addEventListener('utopia:cart-visibility-changed', event => {
    cartEvents.visibility += 1;
    if (event.detail?.open === true) cartState.refresh();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) cartState.refresh(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') cartState.refresh();
  });

  // Older bundled cart classes lack the shared events. Keep their behavior and
  // bridge the hooks; a source implementation that already emits is not doubled.
  if (document.currentScript?.dataset.qdBundledCart === 'true') {
    const bridge = (tag, method, eventName, counter, detail) => {
      customElements.whenDefined(tag).then(() => {
        const prototype = customElements.get(tag).prototype;
        const original = prototype[method];
        if (typeof original !== 'function') return;
        prototype[method] = function(...args) {
          const before = cartEvents[counter];
          const result = original.apply(this, args);
          if (cartEvents[counter] === before) document.dispatchEvent(new CustomEvent(eventName, { detail }));
          return result;
        };
      });
    };
    bridge('cart-items', 'renderContents', 'utopia:cart-updated', 'updated');
    bridge('mini-cart-popup', 'popupBeforeShowExtend', 'utopia:cart-visibility-changed', 'visibility', {open:true});
    bridge('mini-cart-popup', 'popupAfterCloseExtend', 'utopia:cart-visibility-changed', 'visibility', {open:false});
  }

  customElements.define('quantity-discount-cart', class extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-qd-cart-config]').textContent);
        this.list = this.querySelector('[data-qd-cart-campaigns]');
        this.formatter = new Intl.NumberFormat(this.data.locale || 'en', { maximumFractionDigits: 2 });
        this.onVisibility = () => { if (document.visibilityState === 'visible') this.render(); };
        document.addEventListener('visibilitychange', this.onVisibility);
        this.unsubscribe = cartState.subscribe((state, metadata) => {
          this.cart = state;
          this.cartStatus = metadata.status;
          this.snapshot = metadata.snapshot;
          this.render();
        });
      } catch {
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      clearTimeout(this.boundaryTimer);
      this.unsubscribe?.();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    text(key, values = {}) {
      let text = this.data.labels[key] || '';
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll('[' + name + ']', String(value)); });
      return text;
    }

    render() {
      clearTimeout(this.boundaryTimer);
      const view = this.cart || (this.cartStatus === 'loading' ? this.snapshot : null);
      const region = this.querySelector('[data-qd-cart-content]');
      if (!view) {
        loadingRegion(this, region, false, '');
        this.hidden = true; this.list.replaceChildren(); return;
      }
      const now = view.serverNow + performance.now() - view.receivedAt;
      const campaigns = view.campaigns || [];
      const summaries = cartCampaigns(view.items, campaigns, now);
      const delay = boundaryDelay(campaigns, now);
      if (delay !== null) this.boundaryTimer = setTimeout(() => this.render(), delay);
      this.hidden = summaries.length === 0;
      const loading = this.cartStatus === 'loading' && !this.hidden;
      loadingRegion(this, region, loading, this.text('loading'));
      const fragment = document.createDocumentFragment();
      summaries.forEach(campaign => {
        const item = document.createElement('li');
        item.className = 'qd-cart__campaign';
        const name = document.createElement('strong');
        name.className = 'qd-cart__name';
        name.textContent = campaign.name || this.text('title');
        const quantity = document.createElement('p');
        quantity.className = 'qd-cart__quantity';
        quantity.textContent = this.text('quantity', { count: this.formatter.format(campaign.quantity) });
        const tier = document.createElement('p');
        tier.className = 'qd-cart__tier';
        tier.textContent = campaign.percentage ? this.text('discount', { percent: this.formatter.format(campaign.percentage) }) : this.text('no_discount');
        const next = document.createElement('p');
        next.className = 'qd-cart__next';
        next.textContent = campaign.next ? this.text('next', {
          count: this.formatter.format(campaign.next.minimum - campaign.quantity),
          percent: this.formatter.format(campaign.next.percentage)
        }) : this.text('highest');
        const progress = document.createElement('progress');
        progress.max = campaign.maximum;
        progress.value = Math.min(campaign.quantity, campaign.maximum);
        progress.setAttribute('aria-label', name.textContent + ': ' + this.text('progress'));
        progress.setAttribute('aria-valuetext', next.textContent);
        [quantity, tier, next, progress].forEach(node => skeleton(node, loading));
        item.append(name, quantity, tier, next, progress);
        fragment.append(item);
      });
      this.list.replaceChildren(fragment);
    }
  });

  customElements.define('quantity-discount-table', class extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-qd-config]').textContent);
        this.campaign = normalizeCampaign(this.data);
        this.formatter = new Intl.NumberFormat(this.data.locale || 'en', { style: 'currency', currency: this.data.currency });
        this.numberFormatter = new Intl.NumberFormat(this.data.locale || 'en', { maximumFractionDigits: 2 });
        this.labels = this.data.labels;
        this.clock = { serverNow: this.data.serverNow, receivedAt: performance.now() };
        this.content = this.querySelector('[data-qd-content]');
        this.body = this.querySelector('tbody');
        this.cartOpen = Boolean(document.querySelector('#dropdnMinicartPopup')?.closest('.fancybox-is-open'));
        this.onCartVisibility = event => {
          this.cartOpen = event.detail?.open === true;
          this.render();
        };
        this.onQuantity = event => {
          const form = document.getElementById(this.dataset.productFormId);
          if (event.target?.name === 'quantity' && (form?.contains(event.target) || event.target.form === form)) this.render();
        };
        this.onVisibility = () => { if (document.visibilityState === 'visible') this.render(); };
        document.addEventListener('input', this.onQuantity);
        document.addEventListener('change', this.onQuantity);
        document.addEventListener('visibilitychange', this.onVisibility);
        document.addEventListener('utopia:cart-visibility-changed', this.onCartVisibility);
        this.render();
        if (this.campaign) this.unsubscribe = cartState.subscribe((state, metadata) => {
          this.cart = state;
          this.cartStatus = metadata.status;
          this.snapshot = metadata.snapshot;
          if (state) this.clock = state;
          this.render();
        });
      } catch {
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      clearTimeout(this.boundaryTimer);
      this.unsubscribe?.();
      document.removeEventListener('input', this.onQuantity);
      document.removeEventListener('change', this.onQuantity);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('utopia:cart-visibility-changed', this.onCartVisibility);
    }

    money(value) { return this.formatter.format(value / 100); }
    text(key, values = {}) {
      let text = this.labels[key] || '';
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll('[' + name + ']', String(value)); });
      return text;
    }

    selectedQuantity() {
      if (!this.data.available) return 0;
      const form = document.getElementById(this.dataset.productFormId);
      const input = form?.querySelector('[name="quantity"]') || document.querySelector('[name="quantity"][form="' + this.dataset.productFormId + '"]');
      const value = input ? Number(input.value) : 1;
      return Number.isSafeInteger(value) && value > 0 ? value : 0;
    }

    render() {
      clearTimeout(this.boundaryTimer);
      const now = this.clock.serverNow + performance.now() - this.clock.receivedAt;
      const valid = this.campaign && Number.isSafeInteger(this.data.price) && this.data.price >= 0 && Number.isFinite(now);
      const tiers = valid ? activeTiers(this.campaign, now) : [];
      const show = tiers.length > 0;
      this.hidden = !show && !this.data.designMode;
      this.content.hidden = !show;
      this.querySelector('[data-qd-editor-message]').hidden = show || !this.data.designMode;
      if (valid) {
        const boundaries = this.campaign.tiers.flatMap(tier => [tier.startsAt, tier.endsAt]).filter(time => time !== null && time > now);
        if (boundaries.length) this.boundaryTimer = setTimeout(() => this.render(), Math.min(Math.max(50, Math.min(...boundaries) - now + 20), 2147483647));
      }
      const loading = show && this.cartStatus === 'loading' && Boolean(this.snapshot);
      loadingRegion(this, this.querySelector('.qd-table__details'), loading, this.text('loading'));
      if (!show) return;

      const rows = priceRows(this.data.price, tiers);
      const selected = this.cartOpen ? 0 : this.selectedQuantity();
      const view = this.cart || (loading ? this.snapshot : null);
      const cartQuantity = view ? campaignQuantity(view.items, this.campaign.id) : 0;
      const result = estimate(this.data.price, tiers, cartQuantity, selected);
      const fragment = document.createDocumentFragment();
      rows.forEach(row => {
        const tr = document.createElement('tr');
        const current = Boolean(this.cart) && result.projected >= row.minimum && (row.maximum === null || result.projected <= row.maximum);
        tr.classList.toggle('is-current', current);
        if (current) tr.setAttribute('aria-current', 'true');
        const quantity = document.createElement('th');
        quantity.scope = 'row';
        const range = row.maximum === null ? row.minimum + '+' : row.maximum === row.minimum ? String(row.minimum) : row.minimum + '–' + row.maximum;
        const unit = row.minimum === 1 && row.maximum === 1 ? this.data.unitSingular : this.data.unitPlural;
        const rangeLabel = document.createElement('span');
        rangeLabel.className = 'qd-table__range';
        rangeLabel.textContent = range;
        quantity.append(rangeLabel, document.createTextNode(' ' + unit));
        const price = document.createElement('td');
        const amount = document.createElement('strong');
        amount.textContent = this.money(row.price);
        price.append(amount, document.createTextNode(' ' + this.text('each')));
        const savings = document.createElement('td');
        savings.className = 'qd-table__saving';
        if (row.percentage === 0) savings.textContent = this.text('regular');
        else {
          if (this.data.showUnitSaving) savings.append(document.createTextNode(this.text('unit_saving', { amount: this.money(this.data.price - row.price) }) + ' · '));
          if (this.data.showBestValue && row === rows.at(-1)) {
            const badge = document.createElement('span');
            badge.className = 'qd-table__badge';
            badge.textContent = this.text('best_value');
            savings.append(badge);
            const percentage = document.createElement('span');
            percentage.className = 'visually-hidden';
            percentage.textContent = ' ' + this.text('save_percent', { percent: this.numberFormatter.format(row.percentage) });
            savings.append(percentage);
          } else savings.append(document.createTextNode(this.text('save_percent', { percent: this.numberFormatter.format(row.percentage) })));
        }
        tr.append(quantity, price, savings);
        fragment.append(tr);
      });
      this.body.replaceChildren(fragment);

      const summary = this.querySelector('[data-qd-summary]');
      summary.hidden = !this.data.showProgress;
      if (this.data.showProgress) {
        if (view) {
          const key = this.cartOpen ? 'cart_quantity_summary' : 'quantity_summary';
          const values = { cart: cartQuantity, selected, total: result.projected, unit: cartQuantity === 1 ? this.data.unitSingular : this.data.unitPlural };
          const desktop = document.createElement('span');
          desktop.className = 'qd-table__summary-desktop';
          desktop.textContent = this.text(key, values);
          const mobile = document.createElement('span');
          mobile.className = 'qd-table__summary-mobile';
          mobile.textContent = this.text(this.labels[key + '_mobile'] ? key + '_mobile' : key, values);
          summary.replaceChildren(desktop, mobile);
        } else summary.textContent = this.text('cart_unavailable');
      }
      skeleton(summary, loading);
      const saving = this.querySelector('[data-qd-total-saving]');
      saving.hidden = !this.data.showTotalSaving || !view || result.saving <= 0 || selected < 1;
      skeleton(saving, loading);
      if (!saving.hidden) saving.textContent = this.text('total_saving', { count: selected, unit: selected === 1 ? this.data.unitSingular : this.data.unitPlural, amount: this.money(result.saving) });
      const progress = this.querySelector('[data-qd-progress]');
      progress.hidden = !this.data.showProgress || !view;
      skeleton(progress, loading);
      if (!progress.hidden) {
        this.querySelector('[data-qd-next]').textContent = result.next ? this.text('next_tier', { count: result.next.minimum - result.projected, unit: this.data.unitPlural, percent: this.numberFormatter.format(result.next.percentage) }) : this.text('highest_tier');
        const bar = progress.querySelector('progress');
        bar.max = result.next ? result.next.minimum : tiers.at(-1).minimum;
        bar.value = Math.min(result.projected, bar.max);
      }
    }
  });
})();
